/**
 * Tiny pub/sub service for the "Expired Link" modal that pops when a user
 * lands via a notification URL whose `?s=<secret>` has expired. The modal
 * is intentionally minimal — heading, body, single "Resend Link" button —
 * because we have nothing to ask the user beyond "do you want a fresh
 * link?". The secret already binds to a profile + channel, so the BE
 * knows where to re-send.
 */
type State = {
  isOpen: boolean;
  status: 'idle' | 'sending' | 'sent' | 'error';
  errorMessage: string | undefined;
  expiredSecret: string | undefined;
};

type Listener = (state: State) => void;

let state: State = {
  isOpen: false,
  status: 'idle',
  errorMessage: undefined,
  expiredSecret: undefined,
};
const listeners: Set<Listener> = new Set();

function notify() {
  listeners.forEach((l) => l(state));
}

export function openExpiredLinkModal(expiredSecret: string) {
  state = {
    isOpen: true,
    status: 'idle',
    errorMessage: undefined,
    expiredSecret,
  };
  notify();
}

export function setExpiredLinkModalStatus(
  status: State['status'],
  errorMessage?: string,
) {
  state = { ...state, status, errorMessage };
  notify();
}

export function closeExpiredLinkModal() {
  state = {
    isOpen: false,
    status: 'idle',
    errorMessage: undefined,
    expiredSecret: undefined,
  };
  notify();
}

export function getExpiredLinkModalState(): State {
  return state;
}

export function subscribeToExpiredLinkModal(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}
